/**
 * CleverParking (CP) design tokens — ported from the caps-reports reference
 * app so the CRM redesign (v2) shares one visual language.
 *
 * Two consumption paths, kept in sync:
 *   1. CSS custom properties `--cp-*` (see `cp-global.js`) — use `var(--cp-...)`
 *      anywhere, incl. plain CSS and legacy components.
 *   2. This JS token object — for styled-components that prefer `props.theme`.
 *
 * Brand green is identical in light & dark; only surfaces/text/borders swap.
 */

// Brand — shared across themes
export const cpBrand = {
  // Brand green matched to the CleverPark logo (#47b972) — softer, natural,
  // not the previous eye-searing neon.
  green: '#47b972',
  greenBright: '#5cc98a',
  greenDark: '#2f9457',
  warning: '#d4a017',
  error: '#cc2936',
  // "asphalt" — dark header/footer surface, same in both themes
  asphalt: '#0c1210',
  asphaltSoft: '#141c18',
  // signature gradients
  gradient: 'linear-gradient(90deg, #5cc98a 0%, #3aa863 100%)',
  gradientActive: 'linear-gradient(90deg, #3aa863 0%, #2f9457 100%)',
  logoGradient: 'linear-gradient(145deg, #5cc98a 0%, #47b972 60%, #3aa863 100%)',
  primaryShadow: '0 4px 16px rgba(71, 185, 114, 0.3)',
};

export const cpLight = {
  bg: '#fafafa',
  surface: '#ffffff',
  surfaceAlt: '#fafbfc',
  surfaceHover: '#f5f7f6',
  text: '#1c2225',
  textSecondary: '#3a444b',
  textTertiary: '#99a3aa',
  // Stronger, clearly visible borders in light (reviewer: table/filter outlines
  // and row dividers were too pale). `border` = table/filter outline + header
  // rule, `borderSoft` = row dividers — both bumped darker so they read.
  border: '#b7c0c7',
  borderSoft: '#c6cdd3',
  tagBg: '#f0f4f0',
  scrollThumb: '#d1d5db',
  bandAlt: '#f3f5f4',
  // dark, muted red for suspicious rows
  suspicious: '#8a2321',
};

export const cpDark = {
  bg: '#0e1518',
  surface: '#161e23',
  surfaceAlt: '#1a2429',
  surfaceHover: '#232e35',
  text: '#e6eaec',
  textSecondary: '#aab2b8',
  textTertiary: '#7a838a',
  // Stronger, clearly visible borders on dark surfaces (reviewer: borders of
  // fields/tables/cards were invisible on dark).
  border: '#3d4952',
  borderSoft: '#2c353d',
  tagBg: '#1d2a23',
  scrollThumb: '#3a444b',
  bandAlt: '#1a2429',
  // muted, deep red for suspicious rows (not neon)
  suspicious: '#bd433e',
};

// Radii / typography scale (matches antd tokens in the reference)
export const cpScale = {
  radius: '10px',
  radiusLg: '14px',
  radiusSm: '6px',
  radiusPill: '999px',
  fontSize: '13px',
  fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  fontFamilyMono: "'IBM Plex Mono', SFMono-Regular, Menlo, Consolas, monospace",
  headerHeight: '58px',
};

/**
 * Full token bundle for a given mode — handy for `props.theme.cp`.
 * @param {'light'|'dark'} mode
 */
export const getCpTokens = (mode) => ({
  mode,
  ...cpBrand,
  ...(mode === 'dark' ? cpDark : cpLight),
  ...cpScale,
});
